import type { AppointmentWithContext } from "@taskiq/shared";
import { addDays, dayKeyIn, minutesIntoDayIn, toValue, type CalDate } from "./dates";

/** Viewport coordinates of the click that opened a panel. */
export interface Point {
  x: number;
  y: number;
}

/**
 * Opening an appointment carries the element that was clicked, and where in it.
 *
 * The detail panel hangs off the element, so "which one" and "where on screen"
 * are one fact rather than two, and it re-measures that element on scroll so
 * the panel follows. Five surfaces raise this - a chip, a month cell, an agenda
 * row, the dashboard's agenda card and the unassigned tray - and a shared alias
 * is what stops one of them quietly dropping an argument and leaving the panel
 * pinned to the viewport corner.
 *
 * The point exists because two of those five are **full-width rows**. "Beside
 * the anchor" is a good rule for a 90px chip and a meaningless one for a row
 * that spans the screen: there is no beside, so the panel fell through to the
 * clamp and landed in the same place whichever row was clicked. For those, the
 * pointer is the only thing that says which part of the row was meant.
 */
export type OpenAppointment = (
  appointment: AppointmentWithContext,
  anchor: HTMLElement,
  point?: Point | null
) => void;

/**
 * Where the pointer was, or null when the row was reached from the keyboard.
 *
 * `detail` is the click count, and it is **0 for a click synthesised by Enter or
 * Space** on a focused element. Without that check a keyboard user opens every
 * panel at the top-left corner of the window, because `clientX`/`clientY` are
 * both 0 and 0 is a perfectly plausible coordinate.
 */
export function pointerFrom(event: {
  clientX: number;
  clientY: number;
  detail: number;
}): Point | null {
  return event.detail === 0 ? null : { x: event.clientX, y: event.clientY };
}

/**
 * Turning a list of appointments into something a grid can draw.
 *
 * Pure functions over instants and an IANA zone - no React, no DOM. That split
 * is what makes the awkward part (overlap lanes) testable without rendering
 * anything, and it keeps the components to positioning and colour.
 *
 * **Every function here takes the SITE's timezone.** A week grid is a picture of
 * a place, and 9am at a Texas yard is 9am in Texas whoever is looking. See
 * `lib/dates.ts` for the full argument.
 */

export const MINUTES_IN_DAY = 24 * 60;

/** A day column: the calendar date it represents, and what falls in it. */
export interface DayColumn {
  /** "YYYY-MM-DD" in the site's zone. The key events are bucketed by. */
  key: string;
  date: CalDate;
  /** Positioned within the column, overlaps already resolved into lanes. */
  events: PositionedEvent[];
}

export interface PositionedEvent {
  appointment: AppointmentWithContext;
  /** Percent from the top of the column. */
  top: number;
  /** Percent of the column's height. Never less than a legible minimum. */
  height: number;
  /** Percent from the left, and percent wide. Both driven by the lane count. */
  left: number;
  width: number;
}

/** The days a view covers, as calendar dates in the site's zone. */
export function daysFrom(start: CalDate, count: number): CalDate[] {
  return Array.from({ length: count }, (_, i) => addDays(start, i));
}

/**
 * The Monday on or before `date`.
 *
 * Monday rather than Sunday because this is a work schedule: a week that splits
 * the weekend across two views is wrong for a crew that works Saturdays and
 * merely odd for one that does not.
 */
export function startOfWeek(date: CalDate): CalDate {
  // Weekday from the calendar date itself, via a UTC instant used only as
  // scaffolding - a local Date here would shift the day for half the planet.
  const dow = new Date(Date.UTC(date.y, date.m - 1, date.d)).getUTCDay();
  // getUTCDay is 0=Sunday, so Monday-first means Sunday counts as day 7.
  return addDays(date, -((dow + 6) % 7));
}

function startOfMonth(date: CalDate): CalDate {
  return { y: date.y, m: date.m, d: 1 };
}

function daysInMonth(date: CalDate): number {
  return new Date(Date.UTC(date.y, date.m, 0)).getUTCDate();
}

/** Whole days between two calendar dates. UTC scaffolding, never rendered. */
function dayOffset(later: CalDate, earlier: CalDate): number {
  const a = Date.UTC(later.y, later.m - 1, later.d);
  const b = Date.UTC(earlier.y, earlier.m - 1, earlier.d);
  return Math.round((a - b) / 86_400_000);
}

/**
 * The month grid containing `anchor`: whole weeks, Monday-first, spilling into
 * the months either side.
 *
 * **This is also the unit every calendar request is made in**, which is the
 * non-obvious half. The grid starts on the Monday of the week holding the 1st
 * and runs to the Sunday of the week holding the last day, so it contains every
 * week that contains any day of the month - and therefore contains the whole
 * week around *any* anchor inside the month, and the anchor itself. Day, week
 * and month for one anchor are three slices of one fetch, so switching between
 * them is a re-render and not a request. See `monthKey`.
 *
 * At most 42 days, comfortably inside the API's 62-day cap.
 */
export function monthGrid(anchor: CalDate): CalDate[] {
  const first = startOfMonth(anchor);
  const gridStart = startOfWeek(first);
  const needed = daysInMonth(anchor) + dayOffset(first, gridStart);
  return daysFrom(gridStart, Math.ceil(needed / 7) * 7);
}

/** The cache identity of a month grid: "2026-08". */
function monthKey(anchor: CalDate): string {
  return `${anchor.y}-${String(anchor.m).padStart(2, "0")}`;
}

/** The month before or after `anchor`, normalised to its first day. */
export function shiftMonth(anchor: CalDate, direction: 1 | -1): CalDate {
  const m = anchor.m + direction;
  if (m < 1) return { y: anchor.y - 1, m: 12, d: 1 };
  if (m > 12) return { y: anchor.y + 1, m: 1, d: 1 };
  return { y: anchor.y, m, d: 1 };
}

/**
 * The smallest slice of a column an event may occupy, in minutes.
 *
 * The grid draws an hour in 48px (`--weekgrid-day-height`, 72rem over 24
 * hours), so a fifteen-minute appointment is twelve pixels of box. That is not
 * a card - it is a coloured line with a title clipped out of it. An hour is the
 * floor, which is the room a title and its time need at the chip's own type
 * scale.
 *
 * **The value is coupled to that 48px.** Change `--weekgrid-day-height` and
 * this stops meaning what it says, because the minimum that matters is a
 * pixel one and this is its expression in the only unit the positioning maths
 * speaks.
 *
 * The chip does not assume it got exactly this, though. `.weekgrid__event` is a
 * size container and the block chip picks how much to show from the height and
 * width it actually has - one line or two of title, meta or no meta - so the
 * floor guarantees a readable card and the container query spends anything
 * above it. Nothing is drawn and then clipped.
 */
export const MIN_EVENT_MINUTES = 60;

/**
 * Group overlapping events into lanes so they sit side by side.
 *
 * Two passes and no cleverness. First split the day into *clusters* - runs of
 * events that transitively overlap - because lane width has to be decided per
 * cluster, not per day: one clashing pair at 8am should not squeeze
 * every other appointment on that day to half width. Then within a cluster,
 * place each event in the first lane whose previous occupant has already
 * finished.
 *
 * The result is the same arrangement Google Calendar produces, which matters
 * only because it is the arrangement people already know how to read.
 */
function assignLanes(
  items: { appointment: AppointmentWithContext; start: number; end: number }[]
): PositionedEvent[] {
  /**
   * The end each event is **drawn** to, which is not always the end it has.
   *
   * This is the whole reason the minimum is applied here rather than at the
   * point the height is computed. A 15-minute job at 09:00 drawn an hour tall
   * covers 09:00-10:00 of column, so a 09:30 job sitting under it is overlapped
   * by a card it does not overlap in time - the lanes said "these two do not
   * clash" using the real ends, and the rendering then made them clash. Working
   * the clusters and the lanes out from the drawn end is what makes the picture
   * agree with itself: two jobs that *look* like they collide are placed side by
   * side, because as far as this grid is concerned they do.
   *
   * Not clamped to midnight. An appointment starting at 23:50 draws a few pixels
   * past the last hour row, which the scroll container absorbs; clamping it
   * would either shrink the card back to something unreadable or move it up the
   * column, and a card that lies about when it starts is worse than one that
   * overhangs.
   */
  const spans = items.map((item) => ({
    ...item,
    drawnEnd: Math.max(item.end, item.start + MIN_EVENT_MINUTES),
  }));

  const sorted = [...spans].sort((a, b) => a.start - b.start || b.drawnEnd - a.drawnEnd);
  const out: PositionedEvent[] = [];

  let cluster: typeof sorted = [];
  let clusterEnd = -1;

  const flush = () => {
    if (cluster.length === 0) return;

    // Lane index per event, and the running end time of each lane.
    const laneEnds: number[] = [];
    const laneOf = new Map<(typeof cluster)[number], number>();

    for (const item of cluster) {
      let lane = laneEnds.findIndex((end) => end <= item.start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(item.drawnEnd);
      } else {
        laneEnds[lane] = item.drawnEnd;
      }
      laneOf.set(item, lane);
    }

    const lanes = laneEnds.length;
    for (const item of cluster) {
      const lane = laneOf.get(item) ?? 0;
      out.push({
        appointment: item.appointment,
        top: (item.start / MINUTES_IN_DAY) * 100,
        height: ((item.drawnEnd - item.start) / MINUTES_IN_DAY) * 100,
        left: (lane / lanes) * 100,
        width: (1 / lanes) * 100,
      });
    }

    cluster = [];
    clusterEnd = -1;
  };

  for (const item of sorted) {
    if (cluster.length > 0 && item.start >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.drawnEnd);
  }
  flush();

  return out;
}

/**
 * Bucket appointments into day columns and position them.
 *
 * An appointment spanning midnight appears in **both** days, clamped to each -
 * `minutesIntoDayIn` does the clamping. A night shift that vanishes from one of
 * the two days it covers is worse than one drawn twice.
 */
export function buildColumns(
  days: CalDate[],
  appointments: AppointmentWithContext[],
  tz: string
): DayColumn[] {
  return days.map((date) => {
    const key = toValue(date);

    const timed: { appointment: AppointmentWithContext; start: number; end: number }[] = [];

    for (const appointment of appointments) {
      const startKey = dayKeyIn(appointment.scheduled_start, tz);
      const endKey = dayKeyIn(appointment.scheduled_end, tz);
      if (key < startKey || key > endKey) continue;

      const start = minutesIntoDayIn(appointment.scheduled_start, tz, key);
      const end = minutesIntoDayIn(appointment.scheduled_end, tz, key);
      // A zero-height slice happens when an appointment ends exactly at
      // midnight: the following day matches the key but contributes nothing.
      if (end <= start) continue;

      timed.push({ appointment, start, end });
    }

    return { key, date, events: assignLanes(timed) };
  });
}

/**
 * Whether an appointment was scheduled and never happened.
 *
 * Derived here rather than stored, which is why there is no `overdue` value in
 * `appointment_status`: a column would need a cron task to keep it true, and
 * would be wrong for however long that task took to run.
 */
export function isOverdue(appointment: AppointmentWithContext, now = Date.now()): boolean {
  return appointment.status === "scheduled" && Date.parse(appointment.scheduled_end) < now;
}

/** Hour labels down the time axis. `0` is skipped - the column's own top edge says midnight. */
export const HOUR_MARKS = Array.from({ length: 24 }, (_, i) => i);
